import { useEffect, useState } from "react";
import { trackEvent } from "./tracking";

type Props = {
  phone: string;
  bookHref: string;
  phoneLabel?: string;
  bookLabel?: string;
  hideOnPaths?: string[];
  accent?: string;
};

export default function MobileStickyCTA({
  phone,
  bookHref,
  phoneLabel = "Call",
  bookLabel = "Book Assessment",
  hideOnPaths = ["/checkout", "/thanks", "/success"],
  accent = "#f0a500",
}: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    if (hideOnPaths.some((p) => path.startsWith(p))) return;
    const onScroll = () => setVisible(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [hideOnPaths]);

  if (!visible) return null;

  const dial = phone.replace(/[^+\d]/g, "");

  return (
    <div
      role="region"
      aria-label="Mobile quick actions"
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-lg"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
    >
      <div className="flex gap-2 p-2">
        <a
          href={`tel:${dial}`}
          onClick={() => trackEvent("sticky_cta_call", { phone: dial })}
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-gray-900 border-2 text-sm"
          style={{ borderColor: accent }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span>{phoneLabel}</span>
        </a>
        <a
          href={bookHref}
          onClick={() => trackEvent("sticky_cta_book", { href: bookHref })}
          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-white text-sm"
          style={{ backgroundColor: accent }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span>{bookLabel}</span>
        </a>
      </div>
    </div>
  );
}
